export type CardType = 'VISA' | 'MASTERCARD' | 'AMEX' | 'DISCOVER' | 'DINERS' | 'JCB' | 'UNKNOWN';

/**
 * Validate a card number using the Luhn algorithm
 */
export function validateLuhn(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, '');

  if (digits.length < 13 || digits.length > 19) {
    return false;
  }

  let sum = 0;
  let shouldDouble = false;

  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
}

/**
 * Detect the card type based on the card number prefix
 */
export function detectCardType(cardNumber: string): CardType {
  const digits = cardNumber.replace(/\D/g, '');

  if (!digits) return 'UNKNOWN';

  // AMEX: starts with 34 or 37
  if (/^3[47]/.test(digits)) return 'AMEX';

  // VISA: starts with 4
  if (/^4/.test(digits)) return 'VISA';

  // MASTERCARD: starts with 51-55 or 2221-2720
  if (/^5[1-5]/.test(digits) || /^2[2-7]/.test(digits)) return 'MASTERCARD';

  // DISCOVER: starts with 6011, 622126-622925, 644-649, 65
  if (/^(6011|65|64[4-9]|622)/.test(digits)) return 'DISCOVER';

  // DINERS: starts with 300-305, 36, 38
  if (/^(30[0-5]|36|38)/.test(digits)) return 'DINERS';

  // JCB: starts with 2131, 1800, or 35
  if (/^(2131|1800|35)/.test(digits)) return 'JCB';

  return 'UNKNOWN';
}

/**
 * Get the expected CVV length for a given card type
 */
export function getExpectedCVVLength(cardType: CardType): number {
  return cardType === 'AMEX' ? 4 : 3;
}

/**
 * Validate expiry date (not expired and not too far in the future)
 */
export function validateExpiryDate(month: string, year: string): { valid: boolean; error?: string } {
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);

  if (isNaN(m) || m < 1 || m > 12) {
    return { valid: false, error: 'Mes inválido (01-12)' };
  }

  if (isNaN(y)) {
    return { valid: false, error: 'Año inválido' };
  }

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear() % 100;

  if (y < currentYear || (y === currentYear && m < currentMonth)) {
    return { valid: false, error: 'La tarjeta está vencida' };
  }

  // Max 10 years in the future
  if (y > currentYear + 10) {
    return { valid: false, error: 'Año de expiración muy lejano' };
  }

  return { valid: true };
}
